import { useEffect, useMemo, useRef, useState } from "react";
import gsap from "gsap";
import { roundedScaleMaximum } from "./burnstreamMath";

export const BURNSTREAM_SCALE_EXPAND_SECONDS = 0.36;

export function useStickyScale(values: number[], holdMilliseconds = 1_200): number {
  const visibleMaximum = useMemo(() => Math.max(0, ...values), [values]);
  const target = roundedScaleMaximum(visibleMaximum);
  const [maximum, setMaximum] = useState(target);
  useEffect(() => {
    if (target >= maximum) {
      if (target === maximum) return;
      const timer = window.setTimeout(() => setMaximum(target), 0);
      return () => window.clearTimeout(timer);
    }
    const timer = window.setTimeout(() => setMaximum(target), holdMilliseconds);
    return () => window.clearTimeout(timer);
  }, [holdMilliseconds, maximum, target]);
  return Math.max(maximum, target);
}

export function useAnimatedScale(target: number): number {
  const model = useRef({ value: target });
  const [value, setValue] = useState(target);

  useEffect(() => {
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduceMotion || model.current.value === target) {
      model.current.value = target;
      setValue(target);
      return;
    }

    const tween = gsap.to(model.current, {
      value: target,
      duration: target > model.current.value ? BURNSTREAM_SCALE_EXPAND_SECONDS : 0.9,
      ease: "power2.inOut",
      overwrite: true,
      onUpdate: () => setValue(model.current.value),
      onComplete: () => {
        model.current.value = target;
        setValue(target);
      },
    });

    return () => {
      tween.kill();
    };
  }, [target]);

  return value;
}
